import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { TerminalIcon } from "@heroicons/react/outline";
import * as Sentry from "@sentry/browser";
import axios from "axios";
import WebhookSubscriptionForm, {
  WebhookFormSubmit,
} from "src/containers/integrations/Webhooks/WebhookSubscriptionForm";
import HeaderNavigation from "src/components/HeaderNavigation";
import LoaderSpinner from "src/components/LoaderSpinner";
import useCleanTimeout from "src/hooks/useCleanTimeout";
import useWebhookSubscription from "src/hooks/useWebhookSubscription";
import { API_ROUTES, buildAPIRoute, buildRoute } from "src/router/router";
import { axiosErr } from "src/types/axios";
import * as meta from "./Meta";

export default function WebhookSubscriptionUpdate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timeout = useCleanTimeout();

  const webhookId = params.id as string;
  const webhookSubscription = useWebhookSubscription(webhookId);

  const formSubmit: WebhookFormSubmit = async (
    formData,
    setSubmitting,
    setBackendValidationErrors,
    setBackendError
  ) => {
    setBackendValidationErrors({});
    setBackendError("");
    try {
      await axios.put(
        buildAPIRoute(API_ROUTES.WEBHOOK_SUBSCRIPTION_DETAIL, {
          ":id": webhookId,
        }),
        formData,
        {
          headers: {
            "x-module": "webhook",
          },
        }
      );
      setSubmitting(false);
      setSuccess(true);
      timeout.current = setTimeout(() => {
        navigate(buildRoute("webhook-list"));
      }, 1000 * 2);
    } catch (err) {
      const errorAxios: axiosErr = err as axiosErr;
      setSuccess(false);
      if (errorAxios?.response?.status === 400) {
        Sentry.captureException(err);
        setBackendValidationErrors(errorAxios.response.data);
      } else {
        setBackendError(
          t(
            "integrations:webhooks.update.error",
            "An error occurred while updating your webhook subscription. Please try again."
          )
        );
      }
      timeout.current = setTimeout(() => {
        setSubmitted(false);
      }, 1000 * 2);
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  const massagedWebhookSubscription = useMemo<
    meta.WebhookSubscriptionFormValues | undefined
  >(() => {
    if (!webhookSubscription) {
      return undefined;
    }
    const nextEvents = {};
    // go from [event1, event3, ...] to {event1: true, event2: false, event3: true, ...}
    for (const eventName in meta.WEBHOOK_EVENTS) {
      const event = meta.WEBHOOK_EVENTS[eventName];
      const realEventName = meta.WEBHOOK_EVENTS_TRANSLATION[event.value];
      nextEvents[event.value] =
        webhookSubscription.events.includes(realEventName);
    }
    return {
      ...webhookSubscription,
      ...{ events: nextEvents },
    } as meta.WebhookSubscriptionFormValues;
  }, [webhookSubscription]);

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <div className="py-4 px-4 sm:border-b sm:border-gray-200 sm:px-6 lg:px-8">
        <HeaderNavigation
          navPages={[
            {
              name: t("webhooks"),
              url: buildRoute("webhook-list"),
            },
            {
              name: t(
                "integrations:webhooks.update-webhook-subscription",
                "Update Webhook Subscription"
              ),
              url: buildRoute("webhook-update", {
                id: webhookSubscription?.id ?? "",
              }),
            },
          ]}
        />

        <div className="lg:flex lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="mt-2 flex items-center text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              <TerminalIcon className="mr-2 h-7 w-7" aria-hidden="true" />
              {t(
                "integrations:webhooks.update-webhook-subscription",
                "Update Webhook Subscription"
              )}
            </h2>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mt-6 max-w-3xl">
          {!massagedWebhookSubscription ? (
            <div className="flex justify-center">
              <LoaderSpinner />
            </div>
          ) : (
            <WebhookSubscriptionForm
              webhook={massagedWebhookSubscription}
              formSubmit={formSubmit}
              success={success}
              submitted={submitted}
              action="update"
            />
          )}
        </div>
      </div>
    </div>
  );
}
